<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <div class="content">
    <div class="page__title under__line">
      <h1>{{ 'KardexAudit_Title' | translateWF: 'Kardex Audit' }}</h1>
    </div>

    <!-- Search Section -->
    <p-panel header="{{ 'Filters' | translateWF: 'Filters' }}" [toggleable]="false" data-variant="filter">
      <ng-template pTemplate="icons">
        <p-button [label]="'Clear' | translateWF: 'Clear'" (click)="onClear()" icon="pi pi-filter-slash" variant="outlined" size="small" />
        <p-button [label]="'KardexAudit_Search' | translateWF: 'Search'" (click)="onSearch()" icon="pi pi-search" size="small" [disabled]="searchForm.invalid" />
      </ng-template>
      <p-card>
        <div class="grid-auto-4">
          <div class="form-box">
            <label for="searchFromDate">{{ 'KardexAudit_FromDate' | translateWF: 'From' }}</label>
            <p-datepicker
              inputId="searchFromDate"
              [formControl]="searchForm.controls.fromDate"
              dateFormat="mm/dd/yy"
              [showIcon]="true"
              [showButtonBar]="true"
              styleClass="w-full"
              appendTo="body"
            />
          </div>

          <div class="form-box">
            <label for="searchToDate">{{ 'KardexAudit_ToDate' | translateWF: 'To' }}</label>
            <p-datepicker
              inputId="searchToDate"
              [formControl]="searchForm.controls.toDate"
              dateFormat="mm/dd/yy"
              [showIcon]="true"
              [showButtonBar]="true"
              styleClass="w-full"
              appendTo="body"
            />
          </div>

          <div class="form-box">
            <label for="searchItemCode">{{ 'KardexAudit_ItemCode' | translateWF: 'Item Code' }}</label>
            <input
              id="searchItemCode"
              pInputText
              [formControl]="searchForm.controls.itemCode"
              [placeholder]="'KardexAudit_SearchItemCode' | translateWF: 'Search by item code...'"
              class="w-full"
            />
          </div>

          <div class="form-box">
            <label for="searchTransactionTypes">{{ 'KardexAudit_TransactionType' | translateWF: 'Transaction Type' }}</label>
            <p-multiSelect
              inputId="searchTransactionTypes"
              [options]="transactionTypeOptions"
              [formControl]="searchForm.controls.transactionTypes"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'KardexAudit_AllTypes' | translateWF: 'All types'"
              styleClass="w-full"
              appendTo="body"
            />
          </div>

          <div class="form-box">
            <label for="searchSite">{{ 'KardexAudit_Site' | translateWF: 'Site' }}</label>
            <p-select
              inputId="searchSite"
              [options]="siteOptions"
              [formControl]="searchForm.controls.site"
              optionLabel="label"
              optionValue="value"
              styleClass="w-full"
              appendTo="body"
            />
          </div>

          <div class="form-box">
            <label for="searchLotItemNumber">{{ 'KardexAudit_LotItemNumber' | translateWF: 'Roll / Lot #' }}</label>
            <input
              id="searchLotItemNumber"
              pInputText
              [formControl]="searchForm.controls.lotItemNumber"
              [placeholder]="'KardexAudit_SearchLotItem' | translateWF: 'Search by roll or lot number...'"
              class="w-full"
            />
          </div>

          <div class="form-box">
            <label for="searchProductionCardNumber">{{
              'KardexAudit_ProductionCardNumber'
                | translateWF
                  : 'Production Card
              #'
            }}</label>
            <input
              id="searchProductionCardNumber"
              pInputText
              [formControl]="searchForm.controls.productionCardNumber"
              [placeholder]="'KardexAudit_SearchProductionCard' | translateWF: 'Search by production card number...'"
              class="w-full"
            />
          </div>

          <div class="form-box">
            <label for="searchLocation">{{ 'KardexAudit_Location' | translateWF: 'Location' }}</label>
            <input
              id="searchLocation"
              pInputText
              [formControl]="searchForm.controls.location"
              [placeholder]="'KardexAudit_SearchLocation' | translateWF: 'Location name or code (e.g. Batching)...'"
              class="w-full"
            />
          </div>
        </div>
      </p-card>

      <ng-template pTemplate="footer">
        <div class="w-full row-between-center px-2">
          <div class="text-surface-500 text-sm">
            {{
              'KardexAudit_Range_Hint'
                | translateWF
                  : 'Leave dates empty to search across all records without date
            constraints.'
            }}
          </div>
        </div>
      </ng-template>
    </p-panel>

    <p-panel header="{{ 'KardexAudit_Results' | translateWF: 'Results' }}">
      <ng-template pTemplate="icons">
        <p-button
          [label]="'KardexAudit_Export' | translateWF: 'Export'"
          (click)="onExport()"
          icon="pi pi-file-excel"
          severity="success"
          [outlined]="true"
          size="small"
          [disabled]="searchForm.invalid || isExporting()"
          [loading]="isExporting()"
        />
      </ng-template>
      <p-card data-variant="compact">
        <!-- Transactions table -->
        <p-table [value]="transactions()" dataKey="transactionId" [rowHover]="true">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 50px"></th>
              <th>{{ 'KardexAudit_Date' | translateWF: 'Date' }}</th>
              <th>{{ 'KardexAudit_TransactionType' | translateWF: 'Transaction Type' }}</th>
              <th>{{ 'KardexAudit_Status' | translateWF: 'Status' }}</th>
              <th>{{ 'KardexAudit_Movement' | translateWF: 'Movement' }}</th>
              <th>{{ 'KardexAudit_Document' | translateWF: 'Document' }}</th>
              <th>{{ 'KardexAudit_From' | translateWF: 'From' }}</th>
              <th>{{ 'KardexAudit_To' | translateWF: 'To' }}</th>
              <th>{{ 'KardexAudit_Lines' | translateWF: 'Lines' }}</th>
              <th>{{ 'KardexAudit_Qty' | translateWF: 'Qty (lbs)' }}</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-transaction>
            @let tx = txStrict(transaction);
            <tr>
              <td>
                <p-button
                  type="button"
                  (click)="toggleTxRow(tx.transactionId)"
                  [text]="true"
                  [rounded]="true"
                  [plain]="true"
                  [icon]="isTxExpanded(tx.transactionId) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                />
              </td>
              <td>{{ tx.movementDate | hnDate: 'MM/dd/yyyy HH:mm' }}</td>
              <td>{{ transactionTypeLabel(tx.transactionType) }}</td>
              <td>{{ statusLabel(tx.status) }}</td>
              <td>{{ tx.movementDescription }}</td>
              <td>
                <span class="font-semibold">{{ tx.documentType }}</span>
                @if (tx.documentNumber) {
                  <span class="ml-1 text-primary">{{ tx.documentNumber }}</span>
                }
              </td>
              <td>
                {{ (tx.fromLocationName | localized) || tx.fromLocationCode || '—' }} <br />
                <small>{{ tx.fromLocationCode }}</small>
              </td>
              <td>
                {{ (tx.toLocationName | localized) || tx.toLocationCode || '—' }} <br />
                <small>{{ tx.toLocationCode }}</small>
              </td>
              <td>{{ tx.lineCount }}</td>
              <td>{{ tx.totalQuantity | number: '1.2-2' }}</td>
            </tr>

            <!-- Expanded: line contents -->
            @if (isTxExpanded(tx.transactionId)) {
              <tr>
                <td colspan="10" class="p-0">
                  <div class="pl-8 pr-4 py-2 bg-surface-50">
                    <p-table [value]="tx.lines" [rowHover]="true">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'KardexAudit_LineNumber' | translateWF: 'Roll / Lot #' }}</th>
                          <th>{{ 'KardexAudit_LineType' | translateWF: 'Type' }}</th>
                          <th>{{ 'KardexAudit_ItemCode' | translateWF: 'Item Code' }}</th>
                          <th>{{ 'KardexAudit_Qty' | translateWF: 'Qty (lbs)' }}</th>
                          <th>{{ 'KardexAudit_ProductionCard' | translateWF: 'Production Card' }}</th>
                          <th>{{ 'KardexAudit_From' | translateWF: 'From' }}</th>
                          <th>{{ 'KardexAudit_To' | translateWF: 'To' }}</th>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="body" let-txLine>
                        @let line = lineStrict(txLine);
                        <tr>
                          <td>
                            @if (isRollLine(line)) {
                              <a class="text-primary cursor-pointer font-semibold" (click)="openRollHistory(line.number, tx.transactionId)">
                                {{ line.number }}
                              </a>
                            } @else {
                              <span class="font-semibold">{{ line.number }}</span>
                            }
                          </td>
                          <td>{{ isRollLine(line) ? ('KardexAudit_Roll' | translateWF: 'Roll') : ('KardexAudit_Lot' | translateWF: 'Lot') }}</td>
                          <td>{{ line.itemCode || '—' }}</td>
                          <td>{{ line.quantity | number: '1.2-2' }}</td>
                          <td>{{ line.productionCardNumber || '—' }}</td>
                          <td>
                            {{ (line.fromLocationName | localized) || line.fromLocationCode || '—' }} <br />
                            <small>{{ line.fromLocationCode }}</small>
                          </td>
                          <td>
                            {{ (line.toLocationName | localized) || line.toLocationCode || '—' }} <br />
                            <small>{{ line.toLocationCode }}</small>
                          </td>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="7" class="text-center py-4">
                            {{ 'KardexAudit_NoLines' | translateWF: 'No lines' }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                </td>
              </tr>
            }
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="10" class="text-center py-8">
                {{ 'KardexAudit_NoData' | translateWF: 'No transactions found for the selected filters' }}
              </td>
            </tr>
          </ng-template>
        </p-table>

        <p-paginator
          [rows]="pageSize"
          [totalRecords]="totalRecords"
          [first]="(currentPage - 1) * pageSize"
          (onPageChange)="onPageChange($event)"
          [rowsPerPageOptions]="[25, 50, 100]"
        ></p-paginator>
      </p-card>
    </p-panel>
  </div>

  <!-- Roll History Dialog -->
  @if (showRollHistoryDialog() && selectedRollNumber()) {
    <app-kardex-roll-history-dialog
      [rollNumber]="selectedRollNumber()!"
      [highlightTransactionId]="selectedTransactionId()"
      [visible]="showRollHistoryDialog()"
      (closed)="closeRollHistory()"
    />
  }
</main>

<p-toast position="bottom-right" key="br" />
